import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { Table, Skeleton, Select, Typography } from 'antd';
import { SearchOutlined, FilterOutlined, CalendarOutlined } from '@ant-design/icons';

import OverviewHeader from 'common/OverviewHeader';
import { ReactComponent as ZohoIcon } from 'assets/images/icons/zoho.svg';
import AntPagination from 'common/AntTablePagination';
import utils, {  getDecryptedUrl, getUrlWithEncryptedQueryParams } from 'lib/utils';
import { actions as loadingUnloadingActions } from 'redux/loadingUnloading';
import { Link, useHistory } from 'react-router-dom';
import FilterUtils from 'lib/filterUtils';
import useQuery from 'lib/hooks/useQuery';
import dayjs from 'dayjs';
import TextSearchFilter from 'common/Form/AntFilters/TextSearchFilter';
import AutoCompleteFilter from 'common/Form/AntFilters/AutoCompleteFilter';
import RangeDateSearchFilter from 'common/Form/AntFilters/RangeDateSearchFilter';
import OverviewNav from 'common/OverviewNav';
import TableListHeader from 'common/TableListHeader';
import { getLinks } from 'constants/linkConfig';
import CancelIcon from '@material-ui/icons/Cancel';

const ZohoMappingLogs = () => {
  const { page = 1,
    custom_invoice_number,
    invoice_bill_enum,
    created_datetime,
    zoho_invoice_id,
    activeTab,
    exception_message,
    success_fail,
    description,
    zoho_item_code,
    zoho_item_id,
    zoho_account_code,
    zoho_account_id,
    zoho_bill_account_code,
    zoho_bill_account_id,
    category,
    item_used_for,
    address_text,
    zoho_tag_option_code,
    zoho_tag_option_id,
    state_name,
    gstin,
    branch_name,
    zoho_branch_id,
    host,
    vendor_name,
    enum_name,
    counterpart_zoho_vendor_id,
    counterpart_zoho_customer_id,
    person_company_affiliate_enum,
    customer_name,
} = useQuery();

  const dispatch = useDispatch();
  const tableRef = useRef();
  const zohoState = useSelector((state) => state.loadingUnloading);
  const zohoDetails = zohoState?.zohoDetails;
  const userInfo = useSelector((state) => state.user);
    const sid = _.get(userInfo, 'social_profile_id', 0);
    const socialProfiles = useSelector((state) => state.socialProfiles);
    const profileData = _.get(socialProfiles, `data[${sid}]`) || {};
    const partnerId = _.get(profileData, 'partner_id') || null;
  const context = useSelector((state) => state.context);
  const isControlTowerUser = _.get(userInfo, 'profile.person.is_control_tower_user') || false;
  const typeOfIndividualEnum = _.get(userInfo, 'profile.person.type_of_individual_enum') || 0;
  const isTransporter = _.get(userInfo, 'profile.person.is_transporter') || false;
  const busy = zohoState?.busy;
  const [filters, setFilters] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [scrollToFirstRow, setScrollToFirstRow] = useState(false);
  const [links, setLinks] = useState([]);
  const [activeTabTemp, setActiveTabTemp] = useState('');
  const [selectedMappingType, setSelectedMappingType] = useState(null);
  const [zohoMappingTypeEnum, setZohoMappingTypeEnum] = useState(null);
  const isSupplyPartner = _.get(userInfo, 'profile.is_supply_partner') || false
  const back = utils.encodeBackUrl();
  const history = useHistory();
  const loc = getDecryptedUrl(window.location.href);
  const is_zoho_connected = userInfo?.profile?.is_connected;
  let rolesAndRights = useSelector((state) => state.rolesAndRights);
  let rolesAndRightsList = _.get(rolesAndRights, 'entities') || {};

  useEffect(() => {
    if (scrollToFirstRow && tableRef.current) {
      const firstRow = tableRef.current.querySelector('.ant-table-row');
      if (firstRow) {
        firstRow.scrollIntoView({ behavior: 'smooth' });
      }
      setScrollToFirstRow(false);
    }
  }, [scrollToFirstRow]);

  useEffect(() => {
    // Only call API when a mapping type is selected
    if (selectedMappingType && zohoMappingTypeEnum) {
      let filteredParams = FilterUtils.getFilters({
        custom_invoice_number,
        invoice_bill_enum,
        created_datetime,
        zoho_invoice_id,
        exception_message,
        success_fail,
        type: filters.type,
        description,
        zoho_item_code,
        zoho_item_id,
        zoho_account_code,
        zoho_account_id,
        zoho_bill_account_code,
        zoho_bill_account_id,
        category,
        item_used_for,
        address_text,
        zoho_tag_option_code,
        zoho_tag_option_id,
        state_name,
        gstin,
        branch_name,
        zoho_branch_id,
        host,
        vendor_name,
        enum_name,
        counterpart_zoho_vendor_id,
        counterpart_zoho_customer_id,
        person_company_affiliate_enum,
        customer_name,
      });

      const params = {
        filters: filteredParams,
        offset: (parseInt(page) - 1) * rowsPerPage,
        page_size: rowsPerPage,
        zoho_mapping_type_enum: zohoMappingTypeEnum
      };
      dispatch(loadingUnloadingActions.fetchZohoMappingLog(params));
    }
  }, [page, filters, rowsPerPage, selectedMappingType, zohoMappingTypeEnum]);

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    let params = {
      ...filters,
    };
    switch (dataIndex) {
      default:
        delete params[dataIndex];
        setFilters(params);
        history.push(FilterUtils.removeUrl(dataIndex));
        break;
    }
  };

  const onFilterChange = (e, dataIndex, confirm) => {
    confirm();
    let params = {
      ...filters,
    };
    switch (dataIndex) {
      case 'person_company_affiliate_enum': {
        let enumValue = e;
        if (e !== undefined && e !== null && e !== '') {
          if (['1', '2', '3'].includes(String(e))) {
            enumValue = Number(e);
          } else {
            const lowerValue = String(e).toLowerCase().trim();
            if (lowerValue === 'person') {
              enumValue = 1;
            } else if (lowerValue === 'company') {
              enumValue = 2;
            } else if (lowerValue === 'affiliate') {
              enumValue = 3;
            }
          }
        }
        params = {
          ...params,
          [dataIndex]: enumValue,
        };
        break;
      }
      default:
        params = {
          ...params,
          [dataIndex]: e,
        };
        break;
    }
    setFilters(params);
    history.push(FilterUtils.createUrl({ ...params, activeTab: activeTabTemp }));
    setScrollToFirstRow(true);
  };

  const getDateIcon = (filtered, dataIndex) => {
    return (
      <CalendarOutlined
        style={{ color: filters.hasOwnProperty(dataIndex) ? 'var(--unnamed-color-1890ff)' : undefined }}
      />
    );
  };

  const getSearchIcon = (filtered, dataIndex) => {
    return <SearchOutlined style={{ color: filters.hasOwnProperty(dataIndex) ? 'var(--unnamed-color-1890ff)' : undefined }} />;
  };

  const getFilteredIcon = (filtered, dataIndex) => {
    return (
      <FilterOutlined
        style={{ color: filters.hasOwnProperty(dataIndex) ? 'var(--unnamed-color-1890ff)' : undefined }}
      />
    );
  };

  // Column configurations for different mapping types
  const getColumnsByMappingType = (mappingType) => {
    const commonColumns = [
    //   {
    //     title: 'Zoho ID',
    //     dataIndex: 'zoho_id',
    //     width: '120px',
    //     align: 'center',
    //     render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_id || '-'}</div>,
    //   },
    ];

    switch (mappingType) {
      case 'Commodity':
        return [
          ...commonColumns,
          {
            title: 'Commodity Item Description',
            dataIndex: 'description',
            width: '250px',
            align: 'left',
            render: (text, row) => <div className="bfc-table-list-components">{row?.description || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'description'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={description}
                  name={'Commodity Item Description'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'description'),
              filteredValue: description !== undefined ? description : null,
          },
          {
            title: 'Item Name',
            dataIndex: 'zoho_item_code',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_item_code || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_item_code'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_item_code}
                  name={'Item Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_item_code'),
              filteredValue: zoho_item_code !== undefined ? zoho_item_code : null,
          },
          {
            title: 'Item Code',
            dataIndex: 'zoho_item_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_item_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_item_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_item_id}
                  name={'Item Code'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_item_id'),
              filteredValue: zoho_item_id !== undefined ? zoho_item_id : null,
          },
          {
            title: 'Sales Account Name',
            dataIndex: 'zoho_account_code',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_account_code || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_account_code'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_account_code}
                  name={'Sales Account Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_account_code'),
              filteredValue: zoho_account_code !== undefined ? zoho_account_code : null,
          },
          {
            title: 'Sales Account Code',
            dataIndex: 'zoho_account_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_account_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_account_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_account_id}
                  name={'Sales Account Code'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_account_id'),
              filteredValue: zoho_account_id !== undefined ? zoho_account_id : null,
          },
          {
            title: 'Purchase Account Name',
            dataIndex: 'zoho_bill_account_code',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_bill_account_code || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_bill_account_code'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_bill_account_code}
                  name={'Purchase Account Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_bill_account_code'),
              filteredValue: zoho_bill_account_code !== undefined ? zoho_bill_account_code : null,
          },
          {
            title: 'Purchase Account Code',
            dataIndex: 'zoho_bill_account_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_bill_account_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_bill_account_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_bill_account_id}
                  name={'Purchase Account Code'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_bill_account_id'),
              filteredValue: zoho_bill_account_id !== undefined ? zoho_bill_account_id : null,
          },
        ];

        case 'Non Commodity':
            return [
              ...commonColumns,
            //   {
            //     title: 'Main Category of Invoice on platform',
            //     dataIndex: 'category',
            //     width: '250px',
            //     align: 'left',
            //     render: (text, row) => <div className="bfc-table-list-components">{row?.category || '-'}</div>,
            //     filterDropdown: (props) => (
            //         <TextSearchFilter
            //           dataIndex={'category'}
            //           data={props}
            //           onFilterChange={onFilterChange}
            //           handleReset={handleReset}
            //           value={category}
            //           name={'Main Category of Invoice on platform'}
            //         />
            //       ),
            //       filterIcon: (filtered) => getSearchIcon(filtered, 'category'),
            //       filteredValue: category !== undefined ? category : null,
            //   },
              {
                title: 'Description on platform',
                dataIndex: 'description',
                width: '200px',
                align: 'left',
                render: (text, row) => <div className="bfc-table-list-components">{row?.description || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'description'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={description}
                      name={'Description on platform'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'description'),
                  filteredValue: description !== undefined ? description : null,
              },
            //   {
            //     title: 'Item Used For on platform',
            //     dataIndex: 'item_used_for',
            //     width: '200px',
            //     align: 'center',
            //     render: (text, row) => <div className="bfc-table-list-components">{row?.item_used_for || '-'}</div>,
            //     filterDropdown: (props) => (
            //         <TextSearchFilter
            //           dataIndex={'item_used_for'}
            //           data={props}
            //           onFilterChange={onFilterChange}
            //           handleReset={handleReset}
            //           value={item_used_for}
            //           name={'Item Used For on platform'}
            //         />
            //       ),
            //       filterIcon: (filtered) => getSearchIcon(filtered, 'item_used_for'),
            //       filteredValue: item_used_for !== undefined ? item_used_for : null,
            //   },
              {
                title: 'Item Name in Zoho',
                dataIndex: 'zoho_item_code',
                width: '200px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_item_code || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_item_code'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_item_code}
                      name={'Item Name in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_item_code'),
                  filteredValue: zoho_item_code !== undefined ? zoho_item_code : null,
                },
              {
                title: 'Item Id in Zoho',
                dataIndex: 'zoho_item_id',
                width: '200px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_item_id || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_item_id'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_item_id}
                      name={'Item Id in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_item_id'),
                  filteredValue: zoho_item_id !== undefined ? zoho_item_id : null,
                },
              {
                title: 'Sale Account Name in Zoho',
                dataIndex: 'zoho_account_code',
                width: '200px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_account_code || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_account_code'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_account_code}
                      name={'Sale Account Name in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_account_code'),
                  filteredValue: zoho_account_code !== undefined ? zoho_account_code : null,
                },
              {
                title: 'Sale Account ID in Zoho',
                dataIndex: 'zoho_account_id',
                width: '200px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_account_id || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_account_id'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_account_id}
                      name={'Sale Account ID in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_account_id'),
                  filteredValue: zoho_account_id !== undefined ? zoho_account_id : null,
                },
              {
                title: 'Purchase Account Name in Zoho',
                dataIndex: 'zoho_bill_account_code',
                width: '250px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_bill_account_code || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_bill_account_code'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_bill_account_code}
                      name={'Purchase Account Name in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_bill_account_code'),
                  filteredValue: zoho_bill_account_code !== undefined ? zoho_bill_account_code : null,
                },
              {
                title: 'Purchase Account ID in Zoho',
                dataIndex: 'zoho_bill_account_id',
                width: '200px',
                align: 'center',
                render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_bill_account_id || '-'}</div>,
                filterDropdown: (props) => (
                    <TextSearchFilter
                      dataIndex={'zoho_bill_account_id'}
                      data={props}
                      onFilterChange={onFilterChange}
                      handleReset={handleReset}
                      value={zoho_bill_account_id}
                      name={'Purchase Account ID in Zoho'}
                    />
                  ),
                  filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_bill_account_id'),
                  filteredValue: zoho_bill_account_id !== undefined ? zoho_bill_account_id : null,
              },
            ];


      case 'Branch':
        return [
          ...commonColumns,
          {
            title: 'State',
            dataIndex: 'state_name',
            width: '200px',
            align: 'left',
            render: (text, row) => <div className="bfc-table-list-components">{row?.state_name || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'state_name'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={state_name}
                  name={'State'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'state_name'),
              filteredValue: state_name !== undefined ? state_name : null,
          },
          {
            title: 'GSTIN',
            dataIndex: 'gstin',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.gstin || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'gstin'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={gstin}
                  name={'GSTIN'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'gstin'),
              filteredValue: gstin !== undefined ? gstin : null,
          },
          {
            title: 'Branch Name',
            dataIndex: 'branch_name',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.branch_name || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'branch_name'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={branch_name}
                  name={'Branch Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'branch_name'),
              filteredValue: branch_name !== undefined ? branch_name : null,
          },
          {
            title: 'Zoho ID',
            dataIndex: 'zoho_branch_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_branch_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_branch_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_branch_id}
                  name={'Zoho ID'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_branch_id'),
              filteredValue: zoho_branch_id !== undefined ? zoho_branch_id : null,
          },
        ];

      case 'Biomass Bank':
        return [
          ...commonColumns,
          {
            title: 'Storage Name in Platform',
            dataIndex: 'address_text',
            width: '200px',
            align: 'left',
            render: (text, row) => <div className="bfc-table-list-components">{row?.address_text || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'address_text'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={address_text}
                  name={'Storage Name in Platform'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'address_text'),
              filteredValue: address_text !== undefined ? address_text : null,
          },
          {
            title: 'Zoho Warehouse Tag',
            dataIndex: 'zoho_tag_option_code',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_tag_option_code || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_tag_option_code'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_tag_option_code}
                  name={'Zoho Warehouse Tag'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_tag_option_code'),
              filteredValue: zoho_tag_option_code !== undefined ? zoho_tag_option_code : null,
          },
          {
            title: 'Zoho ID',
            dataIndex: 'zoho_tag_option_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.zoho_tag_option_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'zoho_tag_option_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={zoho_tag_option_id}
                  name={'Zoho ID'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'zoho_tag_option_id'),
              filteredValue: zoho_tag_option_id !== undefined ? zoho_tag_option_id : null,
          },
        ];

      case 'Vendor':
        return [
          ...commonColumns,
          {
            title: 'Host',
            dataIndex: 'host',
            width: '200px',
            align: 'left',
            render: (text, row) => <div className="bfc-table-list-components">{row?.host || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'host'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={host}
                  name={'Host'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'host'),
              filteredValue: host !== undefined ? host : null,
          },
          {
            title: 'Vendor Name',
            dataIndex: 'vendor_name',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.vendor_name || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'vendor_name'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={vendor_name}
                  name={'Vendor Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'vendor_name'),
              filteredValue: vendor_name !== undefined ? vendor_name : null,
          },
          {
            title: 'Platform Entity Type',
            dataIndex: 'person_company_affiliate_enum',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.enum_name || '-'}</div>,
            filterDropdown: (props) => (
                <AutoCompleteFilter
                  dataIndex={'person_company_affiliate_enum'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={person_company_affiliate_enum}
                  name={'Platform Entity Type'}
                />
              ),
              filterIcon: (filtered) => getFilteredIcon(filtered, 'person_company_affiliate_enum'),
              filteredValue: person_company_affiliate_enum !== undefined ? person_company_affiliate_enum : null,
          },
          {
            title: 'Zoho Vendor Id',
            dataIndex: 'counterpart_zoho_vendor_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.counterpart_zoho_vendor_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'counterpart_zoho_vendor_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={counterpart_zoho_vendor_id}
                  name={'Zoho Vendor Id'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'counterpart_zoho_vendor_id'),
              filteredValue: counterpart_zoho_vendor_id !== undefined ? counterpart_zoho_vendor_id : null,
          },
        ];

      case 'Customer':
        return [
          ...commonColumns,
          {
            title: 'Host',
            dataIndex: 'host',
            width: '200px',
            align: 'left',
            render: (text, row) => <div className="bfc-table-list-components">{row?.host || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'host'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={host}
                  name={'Host'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'host'),
              filteredValue: host !== undefined ? host : null,
          },
          {
            title: 'Name',
            dataIndex: 'customer_name',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.vendor_name || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'customer_name'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={customer_name}
                  name={'Name'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'customer_name'),
              filteredValue: customer_name !== undefined ? customer_name : null,
          },
          {
            title: 'Platform Entity Type',
            dataIndex: 'person_company_affiliate_enum',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.enum_name || '-'}</div>,
            filterDropdown: (props) => (
                <AutoCompleteFilter
                  dataIndex={'person_company_affiliate_enum'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={person_company_affiliate_enum}
                  name={'Platform Entity Type'}
                />
              ),
              filterIcon: (filtered) => getFilteredIcon(filtered, 'person_company_affiliate_enum'),
              filteredValue: person_company_affiliate_enum !== undefined ? person_company_affiliate_enum : null,
          },
          {
            title: 'Zoho Customer Id',
            dataIndex: 'counterpart_zoho_customer_id',
            width: '200px',
            align: 'center',
            render: (text, row) => <div className="bfc-table-list-components">{row?.counterpart_zoho_customer_id || '-'}</div>,
            filterDropdown: (props) => (
                <TextSearchFilter
                  dataIndex={'counterpart_zoho_customer_id'}
                  data={props}
                  onFilterChange={onFilterChange}
                  handleReset={handleReset}
                  value={counterpart_zoho_customer_id}
                  name={'Zoho Customer Id'}
                />
              ),
              filterIcon: (filtered) => getSearchIcon(filtered, 'counterpart_zoho_customer_id'),
              filteredValue: counterpart_zoho_customer_id !== undefined ? counterpart_zoho_customer_id : null,
          },
        ];

      default:
        return [];
    }
  };

  const TABLE_HEADERS = getColumnsByMappingType(selectedMappingType);

  const handlePageChange = (updatePage) => {
    if (!_.isEmpty(updatePage)) {
      let params = {
        ...filters,
        page: updatePage ? updatePage.current : 1,
      };
      history.push(FilterUtils.createUrl({ ...params, activeTab: activeTabTemp }));
      if (updatePage.pageSize !== rowsPerPage) {
        setRowsPerPage(updatePage.pageSize);
      }
    }
  };

  const clearFilters = () => {
    const { type } = filters;
    const updatedFilters = type ? { type } : {};
    setFilters(updatedFilters);

    const params = new URLSearchParams('');
    params.set('page', 1);
    if (activeTabTemp) {
      params.set('activeTab', activeTabTemp);
    }
    if (type) {
      params.set('type', type);
    }

    let pathname = window.location.pathname;
    history.push(getUrlWithEncryptedQueryParams(`${pathname}?${params.toString()}`));
  };

  const totalRecords = _.get(zohoDetails, `[0].total_count`) || 0;

  // let links = [];
  // links = [
  //   { to: '/market/invoices?page=1', title: 'Invoice' },
  //   { to: '/market/payments?page=1', title: 'Payment' },
  //   { to: '/market/cashflow?page=1', title: 'Cashflow' },
  // ];
  // if (isControlTowerUser && loc.includes('controltower')) {
  //   links = [
  //     { to: '/controltower/invoices?page=1', title: 'Invoice' },
  //     { to: '/controltower/payments?page=1', title: 'Payment' },
  //     { to: '/controltower/manual-invoice?page=1', title: 'Manual Invoice' },
  //   ];
  // }
  // if (isTransporter || typeOfIndividualEnum == 8) {
  //   if (context.ctx == '3') {
  //     links = [
  //       { to: '/network/invoices?page=1', title: 'Invoice' },
  //       { to: '/network/payments?page=1', title: 'Payment' },
  //     ];
  //   }
  // }

  // if (typeOfIndividualEnum === 7) {
  //   if (context.ctx == '3') {
  //     links = [{ to: '/network/payments?page=1', title: 'Payment' }];
  //   }
  // }

  // if (activeTab == "market") {
  //   links.push({ to: `/market/invoices?page=1`, title: 'Invoice' })
  //   links.push({ to: `/market/payments?page=1`, title: 'Payment' })
  //   links.push({ to: `/market/cashflow?page=1`, title: 'Cashflow' })
  //   links.push({ to: `/market/ledger?page=1`, title: 'Ledger' })
  // } else if (activeTab == "network") {
  //   links.push({ to: `/network/ledger?page=1`, title: 'Ledger' })
  // } else if (activeTab == "CT") {
  //   links.push({ to: `/controltower/invoices?page=1`, title: 'Invoice' })
  //   links.push({ to: `/controltower/payments?page=1`, title: 'Payment' })
  //   links.push({ to: '/controltower/manual-invoice?page=1', title: 'Manual Invoice' })
  //   links.push({ to: `/control-tower/ledger?page=1`, title: 'Ledger' })
  // }

  // if (is_zoho_connected) {
  //   links = [...links, { title: 'Zoho Invoice Log', to: `/configuration/zoho-logs?page=1&activeTab=${activeTab}` }, { title: 'Zoho Payment Log', to: `/configuration/zoho_payment-log?page=1&activeTab=${activeTab}` }]
  // }

  useEffect(() => {
    const fetchedLinks = getLinks(isSupplyPartner,isControlTowerUser, isTransporter, typeOfIndividualEnum, context, loc, is_zoho_connected, activeTab, partnerId,userInfo,rolesAndRightsList);
    setLinks(fetchedLinks);
  // }, [loc, filters]);
  }, [context.ctx]);
  // }, [filters]);

  useEffect(() => {
    if (context.ctx === '2') {
      setActiveTabTemp('market');
    } else if (context.ctx === '4') {
      setActiveTabTemp('network');
    } else if (context.ctx === '5') {
      setActiveTabTemp('CT');
    }
  }, [context.ctx])

  return (
    <div>
      {/* <OverviewNav links={links} /> */}
      <div className="bfc-header">
        <TableListHeader
          links={links} />
            <Select
            placeholder="Select Mapping Type"
            style={{ width: 200 }}
            value={selectedMappingType}
            // allowClear
            onChange={(value) => {
              if (!value) {
                clearFilters();
                setSelectedMappingType(null);
                setZohoMappingTypeEnum(null);
                return;
              }

              const mappingConfig = {
                'Commodity': { type: 'Commodity', enum: 1 },
                'Non Commodity': { type: 'Non-Commodity', enum: 1 },
                'Branch': { type: 'Branch', enum: 2 },
                'Biomass Bank': { type: 'Biomass Bank', enum: 3 },
                'Vendor': { type: 'Vendor', enum: 4 },
                'Customer': { type: 'Customer', enum: 5 }
              };

              const config = mappingConfig[value];
              if (config) {
                const params = new URLSearchParams('');
                params.set('page', 1);
                if (activeTabTemp) {
                  params.set('activeTab', activeTabTemp);
                }
                const shouldSetType = value === 'Commodity' || value === 'Non Commodity';
                if (shouldSetType && config.type) {
                  params.set('type', config.type);
                }

                setSelectedMappingType(value);
                setZohoMappingTypeEnum(config.enum);
                setFilters(shouldSetType && config.type ? { type: config.type } : {});
                history.push(getUrlWithEncryptedQueryParams(`${window.location.pathname}?${params.toString()}`));
                // setScrollToFirstRow(true);
              }
            }}
          >
            <Select.Option value="Commodity">Commodity</Select.Option>
            <Select.Option value="Non Commodity">Non Commodity</Select.Option>
            <Select.Option value="Branch">Branch</Select.Option>
            <Select.Option value="Biomass Bank">Biomass Bank</Select.Option>
            <Select.Option value="Vendor">Vendor</Select.Option>
            <Select.Option value="Customer">Customer</Select.Option>
          </Select>
      </div>
      <div className="bfc-body">
        {/* {busy && (
          <div className="commodity-wrapper">
            <div className="panel__refresh">
              <LoadingIcon />
            </div>
          </div>
        )} */}
        <div className="blocked-table">
            {/* <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <Typography.Title level={5}>{selectedMappingType ? selectedMappingType : 'Mapping Type'}</Typography.Title>
                <Select
                  placeholder="Select Mapping Type"
                  style={{ width: 200 }}
                  value={selectedMappingType}
                  allowClear
                  onChange={(value) => {
                    if (!value) {
                      // Clear selection
                      setSelectedMappingType(null);
                      setZohoMappingTypeEnum(null);
                      setFilters(prev => {
                        const newFilters = { ...prev };
                        delete newFilters.type;
                        return newFilters;
                      });
                      return;
                    }

                    const mappingConfig = {
                      'Commodity': { type: 'Commodity', enum: 1 },
                      'Non Commodity': { type: 'Non-Commodity', enum: 1 },
                      'Branch': { type: 'Branch', enum: 2 },
                      'Biomass Bank': { type: 'Biomass Bank', enum: 3 },
                      'Vendor': { type: 'Vendor', enum: 4 },
                      'Customer': { type: 'Customer', enum: 5 }
                    };

                    const config = mappingConfig[value];
                    if (config) {
                      setSelectedMappingType(value);
                      setZohoMappingTypeEnum(config.enum);
                      setFilters(prev => ({
                        ...prev,
                        type: config.type
                      }));
                    }
                  }}
                >
                  <Select.Option value="Commodity">Commodity</Select.Option>
                  <Select.Option value="Non Commodity">Non Commodity</Select.Option>
                  <Select.Option value="Branch">Branch</Select.Option>
                  <Select.Option value="Biomass Bank">Biomass Bank</Select.Option>
                  <Select.Option value="Vendor">Vendor</Select.Option>
                  <Select.Option value="Customer">Customer</Select.Option>
                </Select>
            </div> */}
          <div className="ant-table-body ant-table">
            {!selectedMappingType ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Typography.Text type="secondary">
                  Please select a mapping type to view the mapping logs.
                </Typography.Text>
              </div>
            ) : (
              <>
                <Table
                  ref={tableRef}
                  dataSource={!busy ? zohoDetails : [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}]}
                  columns={busy ? TABLE_HEADERS.map((column) => {
                    return {
                      ...column,
                      render: function renderPlaceholder() {
                        return (
                          <Skeleton
                            active="true"
                            key={column.dataIndex}
                            title={true}
                            paragraph={false}
                          />
                        );
                      },
                    };
                  }) : TABLE_HEADERS}
                  pagination={false}
                  onChange={handlePageChange}
                  scroll={{ y: 550 }}
                  className="ant-table"
                  size="small"
                />
                {totalRecords > 0 && (
                  <AntPagination
                    total={parseInt(totalRecords)}
                    handlePageChange={handlePageChange}
                    page={page}
                    clearFilters={clearFilters}
                    rowsPerPage={rowsPerPage}
                    setScrollToFirstRow={setScrollToFirstRow}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default ZohoMappingLogs;
